import React, { useEffect, useState } from "react";
import { Trash2, Mail, Building2, User, RefreshCw } from "lucide-react";
import api from "../../../lib/api";
import { useToast } from "../../../hooks/use-toast";
import { SectionTitle } from "./_shared";

const UNIT_COLORS = {
  music: { bg: "bg-[#ff6a2b]/15", border: "border-[#ff6a2b]/30", text: "text-[#ff8a3c]" },
  teams: { bg: "bg-[#2ec9a8]/15", border: "border-[#2ec9a8]/30", text: "text-[#2ec9a8]" },
  general: { bg: "bg-white/10", border: "border-white/20", text: "text-white" },
};

export default function MessagesPanel() {
  const { toast } = useToast();
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contact");
      setMsgs(data);
    } catch (e) {
      toast({ title: "Error", description: "No se pudieron cargar los mensajes." });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    try {
      await api.delete(`/contact/${id}`);
      setMsgs((m) => m.filter((x) => x.id !== id));
      toast({ title: "Eliminado" });
    } catch (e) {
      toast({ title: "Error", description: "No se pudo eliminar." });
    }
  };

  return (
    <div>
      <SectionTitle
        title="Mensajes de contacto"
        subtitle={`${msgs.length} mensajes recibidos`}
        right={
          <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/30 text-white/70 hover:text-white text-sm transition-colors">
            <RefreshCw className="w-4 h-4" /> Actualizar
          </button>
        }
      />

      {loading ? (
        <p className="text-white/50">Cargando...</p>
      ) : msgs.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-black/40 p-10 text-center">
          <Mail className="w-8 h-8 text-white/30 mx-auto mb-3" />
          <p className="text-white/60">Aún no hay mensajes.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {msgs.map((m) => {
            const c = UNIT_COLORS.general;
            const date = new Date(m.created_at).toLocaleString("es-CO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
            const fullName = [m.name, m.lastname].filter(Boolean).join(" ");
            return (
              <div key={m.id} className="rounded-xl border border-white/10 bg-black/40 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${c.bg} ${c.border} border ${c.text} text-xs font-mono-caps tracking-[0.15em] uppercase`}>Mensaje</span>
                    <span className="text-white/40 text-xs">{date}</span>
                  </div>
                  <button onClick={() => remove(m.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-colors text-xs">
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-white/80"><User className="w-4 h-4 text-white/40" /> {fullName}</div>
                  <a href={`mailto:${m.email}`} className="flex items-center gap-2 text-white/80 hover:text-[#ff8a3c] transition-colors"><Mail className="w-4 h-4 text-white/40" /> {m.email}</a>
                  {m.company && <div className="flex items-center gap-2 text-white/80"><Building2 className="w-4 h-4 text-white/40" /> {m.company}</div>}
                  {m.phone && <div className="flex items-center gap-2 text-white/80"><span className="w-4 h-4 text-white/40">☎</span> {m.phone}</div>}
                </div>
                <p className="mt-4 text-white/75 text-sm leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
